import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type WheelEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { createPublicClient, createWalletClient, custom, getAddress, http, type Address } from 'viem'
import { base } from 'viem/chains'
import { InstalledInjectedWalletPicker } from '../components/InstalledInjectedWalletPicker'
import { MobileWalletPayPanel } from '../components/MobileWalletPayPanel'
import { UsdcBaseCompositeIcon } from '../components/UsdcBaseCompositeIcon'
import { UsdcTopupSiteHeader } from '../components/UsdcTopupSiteHeader'
import {
	isMobileDeviceForWalletApps,
	subscribeInstalledInjectedWallets,
	type Eip1193Provider,
	type InjectedWalletChoice,
} from '../utils/mobileWalletApps'
import {
	encodeX402PaymentPayload,
	isDesktopCoinbaseWalletExtension,
	requestEthSignTypedDataV4,
	TRANSFER_WITH_AUTHORIZATION_TYPES,
	wrapCoinbaseExtensionProvider,
} from '../utils/coinbaseExtensionSafeSign'
import { lookupFuelPack } from '../utils/fuelPackCatalog'
import { fetchUsdcBalanceOnBase } from '../utils/beamioUsdcBalance'

declare global {
	interface Window {
		ethereum?: Eip1193Provider
	}
}

const BEAMIO_API = 'https://beamio.app'
const BASE_CHAIN_ID_HEX = '0x2105'
const BASE_USDC_ADDRESS = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913' as Address
const BASE_CADD_ADDRESS = '0x16F93eBC5320C89EfC8701577efe49d14A276a06' as Address
const WALLET_REQUEST_TIMEOUT_MS = 60_000
const WALLET_SIGNATURE_TIMEOUT_MS = 120_000

function withWalletTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const timer = window.setTimeout(() => reject(new Error(message)), timeoutMs)
		promise.then(
			(value) => {
				window.clearTimeout(timer)
				resolve(value)
			},
			(error) => {
				window.clearTimeout(timer)
				reject(error)
			},
		)
	})
}

type Status =
	| 'idle'
	| 'connecting'
	| 'switching-chain'
	| 'quoting'
	| 'preparing-payment'
	| 'awaiting-signature'
	| 'settling'
	| 'success'
	| 'error'

type QuoteResponse = {
	success?: boolean
	error?: string
	quotedUsdc6?: string
	quotedUsdc?: string
	currency?: string
	amount?: string
	cardOwner?: string
	permitSpender?: string
}

type TopupParams = {
	cardAddress: string
	cardOwner: string
	/** 无 NFC 的 POS 两阶段 QR 可省略；有值时需与 e/c/m 成套 */
	uid: string
	e: string
	c: string
	m: string
	amount: string
	currency: string
	/** POS 轮询 `nfcUsdcChargeSession` 的 UUID v4；与 `pos` 成对出现在 POS 生成的 QR 上 */
	sid: string
	/** POS 终端 admin EOA（与 `sid` 成对）；后端据此走 POS 签 ExecuteForAdmin 闭环 */
	pos: string
	/** 非 admin 消费者 clientTopup（遗留）：USDC 结算到此 EOA；卡内入账由客户端 App 完成 */
	beneficiary: string
	/** Discover 国库桥：卡点 #0 入账目标 Smart Wallet */
	aa: string
	/** Genesis Seat：购买节点数量 */
	qty: number
	workflow: '' | 'clientTopup' | 'treasuryBridge' | 'genesisNodeSeat' | 'walletDeposit' | 'fuelPack'
	paymentToken: 'USDC' | 'CADD'
	/** Evangelist L0 EOA (optional Genesis share attribution). */
	referrerL0: string
	/** Custom Fuel catalog id (`genesis_starter`, …); empty for custom USDC amount. */
	packId: string
	/** Discover membership first-issue / upgrade (treasuryBridge). */
	membershipTierIndex: number | null
	membershipFeeFiat6: string
}

const truncate = (s: string, head = 6, tail = 4): string =>
	s && s.length > head + tail + 3 ? `${s.slice(0, head)}…${s.slice(-tail)}` : s

const isHex = (s: string, len?: number): boolean =>
	typeof s === 'string' && /^[0-9a-fA-F]+$/.test(s) && (len === undefined || s.length === len)

const isEthAddress = (s: string): boolean => typeof s === 'string' && /^0x[0-9a-fA-F]{40}$/.test(s)

const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
/** Must match x402sdk `GENESIS_NODE_SEAT_USDC_PER_NODE6` (4000 USDC list price, OPEX included). */
const GENESIS_NODE_SEAT_USDC_PER_NODE6 = 4_000_000_000n
const isUuidV4 = (s: string): boolean => typeof s === 'string' && UUID_V4_RE.test(s)

function parseParams(sp: URLSearchParams): { ok: true; params: TopupParams } | { ok: false; error: string } {
	const queryGetCI = (...keys: string[]): string => {
		for (const key of keys) {
			const direct = sp.get(key)
			if (direct !== null) return direct
		}
		const folded = keys.map((k) => k.trim().toLowerCase())
		for (const [k, v] of sp.entries()) {
			if (folded.includes(k.trim().toLowerCase())) return v
		}
		return ''
	}
	const cardAddress = (sp.get('card') ?? '').trim()
	const cardOwner = (sp.get('owner') ?? '').trim()
	const uid = (sp.get('uid') ?? '').trim()
	const e = (sp.get('e') ?? '').trim()
	const c = (sp.get('c') ?? '').trim()
	const m = (sp.get('m') ?? '').trim()
	const amount = (sp.get('amount') ?? '').trim()
	const currency = (sp.get('currency') ?? 'CAD').trim().toUpperCase()
	const tokenRaw = queryGetCI('paymentToken', 'payToken').trim().toUpperCase()
	const paymentToken: 'USDC' | 'CADD' = tokenRaw === 'CADD' || currency === 'CADD' ? 'CADD' : 'USDC'
	const sid = (sp.get('sid') ?? '').trim().toLowerCase()
	const pos = (sp.get('pos') ?? '').trim()
	const beneficiary = (sp.get('beneficiary') ?? '').trim()
	const aa = (queryGetCI('aa', 'recipientAA') || '').trim()
	const qtyRaw = (queryGetCI('qty', 'quantity') || '').trim()
	const referrerL0Raw = (queryGetCI('referrerL0', 'referrer') || '').trim()
	const workflowRaw = queryGetCI('workflow').trim().toLowerCase()
	const workflow: '' | 'clientTopup' | 'treasuryBridge' | 'genesisNodeSeat' | 'walletDeposit' | 'fuelPack' =
		workflowRaw === 'treasurybridge'
			? 'treasuryBridge'
			: workflowRaw === 'clienttopup'
				? 'clientTopup'
				: workflowRaw === 'genesisnodeseat'
					? 'genesisNodeSeat'
					: workflowRaw === 'fuelpack'
						? 'fuelPack'
						: workflowRaw === 'walletdeposit'
							? 'walletDeposit'
							: ''
	if (workflow !== 'walletDeposit' && workflow !== 'fuelPack') {
		if (!isEthAddress(cardAddress)) return { ok: false, error: 'Missing or invalid `card` (BeamioUserCard address)' }
		if (!isEthAddress(cardOwner)) return { ok: false, error: 'Missing or invalid `owner` (card owner EOA)' }
	}
	const packRaw = queryGetCI('pack', 'packId').trim()
	const catalogPack = lookupFuelPack(packRaw)
	let amountResolved = amount
	if (workflow === 'fuelPack') {
		if (packRaw && !catalogPack) {
			return { ok: false, error: 'Unknown fuel pack' }
		}
		if (catalogPack) {
			if (!amountResolved) amountResolved = catalogPack.usdcAmount
			else if (Number(amountResolved) !== Number(catalogPack.usdcAmount)) {
				return { ok: false, error: `fuelPack amount must be ${catalogPack.usdcAmount} USDC` }
			}
		}
	}
	if (workflow === 'treasuryBridge') {
		if (!isEthAddress(aa)) {
			return { ok: false, error: 'Missing or invalid `aa` (Smart Wallet) for treasuryBridge workflow' }
		}
		if (sid || pos) {
			return { ok: false, error: 'treasuryBridge links must not include `sid` or `pos` (POS admin workflow)' }
		}
	} else if (workflow === 'clientTopup') {
		if (!isEthAddress(beneficiary)) {
			return { ok: false, error: 'Missing or invalid `beneficiary` (consumer EOA) for clientTopup workflow' }
		}
		if (sid || pos) {
			return { ok: false, error: 'clientTopup links must not include `sid` or `pos` (POS admin workflow)' }
		}
	} else if (workflow === 'genesisNodeSeat') {
		if (!isEthAddress(beneficiary)) {
			return { ok: false, error: 'Missing or invalid `beneficiary` (buyer EOA) for genesisNodeSeat workflow' }
		}
		const qtyNum = Number(qtyRaw)
		if (!Number.isInteger(qtyNum) || qtyNum < 1 || qtyNum > 12000) {
			return { ok: false, error: 'Missing or invalid `qty` (positive integer node count) for genesisNodeSeat' }
		}
		if (sid || pos) {
			return { ok: false, error: 'genesisNodeSeat links must not include `sid` or `pos` (POS admin workflow)' }
		}
		if (currency !== 'USDC' && paymentToken !== 'USDC') {
			return { ok: false, error: 'genesisNodeSeat requires currency/paymentToken USDC' }
		}
		if (referrerL0Raw && !isEthAddress(referrerL0Raw)) {
			return { ok: false, error: 'Invalid `referrerL0` (expect EOA address)' }
		}
	} else if (workflow === 'walletDeposit') {
		if (!isEthAddress(beneficiary)) {
			return { ok: false, error: 'Missing or invalid `beneficiary` (wallet EOA or AA) for walletDeposit workflow' }
		}
		if (sid || pos) {
			return { ok: false, error: 'walletDeposit links must not include `sid` or `pos` (POS admin workflow)' }
		}
		if (currency !== 'USDC' && paymentToken !== 'USDC') {
			return { ok: false, error: 'walletDeposit requires currency/paymentToken USDC' }
		}
	} else if (workflow === 'fuelPack') {
		if (!isEthAddress(beneficiary)) {
			return { ok: false, error: 'Missing or invalid `beneficiary` (merchant EOA) for fuelPack workflow' }
		}
		if (sid || pos) {
			return { ok: false, error: 'fuelPack links must not include `sid` or `pos` (POS admin workflow)' }
		}
		if (currency !== 'USDC' && paymentToken !== 'USDC') {
			return { ok: false, error: 'fuelPack requires currency/paymentToken USDC' }
		}
	} else {
		if (sid && !isUuidV4(sid)) return { ok: false, error: 'Invalid `sid` (expect UUID v4)' }
		if (sid && !isEthAddress(pos)) return { ok: false, error: 'Missing or invalid `pos` when `sid` is set (POS terminal EOA)' }
		if (!sid && pos && !isEthAddress(pos)) return { ok: false, error: 'Invalid `pos` (expect checksummed EOA)' }
	}
	const hasSidPos = Boolean(sid && isEthAddress(pos))
	const treasuryBridgePath = workflow === 'treasuryBridge' && isEthAddress(aa)
	const clientTopupPath = workflow === 'clientTopup' && isEthAddress(beneficiary)
	const genesisSeatPath = workflow === 'genesisNodeSeat' && isEthAddress(beneficiary)
	const walletDepositPath = workflow === 'walletDeposit' && isEthAddress(beneficiary)
	const fuelPackPath = workflow === 'fuelPack' && isEthAddress(beneficiary)
	const genesisQty = genesisSeatPath ? Number(qtyRaw) : 0
	if (!hasSidPos && !clientTopupPath && !treasuryBridgePath && !genesisSeatPath && !walletDepositPath && !fuelPackPath) {
		if (!uid || !isHex(uid, 14)) return { ok: false, error: 'Missing or invalid `uid` (NFC UID, 14 hex chars)' }
		if (!isHex(e, 64)) return { ok: false, error: 'Missing or invalid SUN `e` (64 hex chars)' }
		if (!isHex(c, 6)) return { ok: false, error: 'Missing or invalid SUN `c` (6 hex chars)' }
		if (!isHex(m, 16)) return { ok: false, error: 'Missing or invalid SUN `m` (16 hex chars)' }
	} else {
		if (uid) {
			if (!isHex(uid, 14)) return { ok: false, error: 'Invalid `uid` (expect 14 hex chars)' }
			if (!isHex(e, 64) || !isHex(c, 6) || !isHex(m, 16)) {
				return { ok: false, error: 'When `uid` is present, SUN params `e`, `c`, `m` are required' }
			}
		}
	}
	const amountOk = !!amountResolved && Number(amountResolved) > 0
	if (!amountOk && !walletDepositPath) {
		return { ok: false, error: 'Missing or invalid `amount`' }
	}
	if (!currency) return { ok: false, error: 'Missing `currency`' }
	const membershipTierRaw = queryGetCI('membershipTierIndex').trim()
	const membershipFeeFiat6Raw = queryGetCI('membershipFeeFiat6').trim()
	let membershipTierIndex: number | null = null
	let membershipFeeFiat6 = ''
	if (/^\d+$/.test(membershipTierRaw) && /^\d+$/.test(membershipFeeFiat6Raw)) {
		try {
			if (BigInt(membershipFeeFiat6Raw) > 0n) {
				membershipTierIndex = Number(membershipTierRaw)
				membershipFeeFiat6 = membershipFeeFiat6Raw
			}
		} catch {
			membershipTierIndex = null
			membershipFeeFiat6 = ''
		}
	}
	return {
		ok: true,
		params: {
			cardAddress,
			cardOwner,
			uid: hasSidPos && !uid ? '' : uid,
			e: hasSidPos && !uid ? '' : e,
			c: hasSidPos && !uid ? '' : c,
			m: hasSidPos && !uid ? '' : m,
			amount: walletDepositPath && !amountOk ? '' : amountResolved,
			currency: genesisSeatPath || walletDepositPath || fuelPackPath ? 'USDC' : currency,
			sid,
			pos: pos && isEthAddress(pos) ? pos : '',
			beneficiary: clientTopupPath || genesisSeatPath || walletDepositPath || fuelPackPath ? beneficiary : '',
			aa: treasuryBridgePath ? aa : '',
			qty: genesisSeatPath ? genesisQty : 0,
			workflow: treasuryBridgePath
				? 'treasuryBridge'
				: clientTopupPath
					? 'clientTopup'
					: genesisSeatPath
						? 'genesisNodeSeat'
						: fuelPackPath
							? 'fuelPack'
							: walletDepositPath
								? 'walletDeposit'
								: '',
			paymentToken: genesisSeatPath || walletDepositPath || fuelPackPath ? 'USDC' : paymentToken,
			referrerL0: genesisSeatPath && isEthAddress(referrerL0Raw) ? referrerL0Raw : '',
			packId: fuelPackPath && catalogPack ? catalogPack.id : '',
			membershipTierIndex,
			membershipFeeFiat6,
		},
	}
}

function applyMembershipPayFieldsToBody(bodyObj: Record<string, string>, p: TopupParams): void {
	if (p.workflow !== 'treasuryBridge') return
	if (p.membershipTierIndex == null || p.membershipTierIndex < 0) return
	if (!p.membershipFeeFiat6 || !/^\d+$/.test(p.membershipFeeFiat6)) return
	bodyObj.membershipTierIndex = String(p.membershipTierIndex)
	bodyObj.membershipFeeFiat6 = p.membershipFeeFiat6
}

/** Long enough for quote review; Coinbase Pay must hit a warm challenge (no await before sign). */
const X402_CHALLENGE_TTL_MS = 90_000
const X402_PREFETCH_MS = 12_000
const CADD_PREP_TTL_MS = 30_000

type UsdcTopupBodyBuild =
	| { ok: true; bodyObj: Record<string, string>; body: string; x402MaxValue: bigint }
	| { ok: false; error: string }

function buildUsdcNfcTopupBody(p: TopupParams, payAmount: string): UsdcTopupBodyBuild {
	if (p.workflow === 'walletDeposit' && !payAmount) {
		return { ok: false, error: 'Enter a valid USDC amount greater than 0' }
	}
	const amountAtomic6 = decimalToAtomic6(payAmount)
	let x402MaxValue =
		amountAtomic6 && BigInt(amountAtomic6) > 0n ? BigInt(amountAtomic6) : 1_000_000_000n
	if (p.workflow === 'genesisNodeSeat' && p.qty > 0) {
		const genesisFloor = BigInt(p.qty) * GENESIS_NODE_SEAT_USDC_PER_NODE6
		if (genesisFloor > x402MaxValue) x402MaxValue = genesisFloor
	}
	const bodyObj: Record<string, string> = {
		amount: payAmount,
		currency: p.currency,
	}
	if (p.workflow !== 'walletDeposit' && p.workflow !== 'fuelPack') {
		bodyObj.cardAddress = p.cardAddress
		bodyObj.cardOwner = p.cardOwner
	}
	if (p.workflow === 'treasuryBridge' && p.aa) {
		bodyObj.aa = p.aa
		bodyObj.workflow = 'treasuryBridge'
	} else if (p.workflow === 'genesisNodeSeat' && p.beneficiary) {
		bodyObj.beneficiary = p.beneficiary
		bodyObj.qty = String(p.qty)
		bodyObj.workflow = 'genesisNodeSeat'
		if (p.referrerL0) bodyObj.referrerL0 = p.referrerL0
	} else if (p.workflow === 'walletDeposit' && p.beneficiary) {
		bodyObj.beneficiary = p.beneficiary
		bodyObj.workflow = 'walletDeposit'
	} else if (p.workflow === 'fuelPack' && p.beneficiary) {
		bodyObj.beneficiary = p.beneficiary
		bodyObj.workflow = 'fuelPack'
		if (p.packId) bodyObj.pack = p.packId
	} else if (p.workflow === 'clientTopup' && p.beneficiary) {
		bodyObj.beneficiary = p.beneficiary
		bodyObj.workflow = 'clientTopup'
	} else {
		if (p.sid) bodyObj.sid = p.sid
		if (p.pos) bodyObj.pos = p.pos
		if (p.uid) {
			bodyObj.uid = p.uid
			bodyObj.e = p.e
			bodyObj.c = p.c
			bodyObj.m = p.m
		}
	}
	applyMembershipPayFieldsToBody(bodyObj, p)
	return { ok: true, bodyObj, body: JSON.stringify(bodyObj), x402MaxValue }
}

function caddPermitValueFromQuote(p: TopupParams, quote: QuoteResponse | null): string {
	const effectiveCurrency = (quote?.currency ?? p.currency ?? '').trim().toUpperCase()
	const caddCadDirect = effectiveCurrency === 'CAD' || effectiveCurrency === 'CADD'
	if (caddCadDirect) return decimalToAtomic6(p.amount) ?? ''
	const quoted = quote?.quotedUsdc6?.trim()
	if (quoted && /^\d+$/.test(quoted) && BigInt(quoted) > 0n) return quoted
	const n = Number(quote?.quotedUsdc ?? '')
	return Number.isFinite(n) && n > 0 ? BigInt(Math.round(n * 1_000_000)).toString() : ''
}

type X402SelectedReq = {
	maxAmountRequired: string
	asset: string
	extra?: { name?: string; version?: string }
}

type X402ChallengeCache = {
	key: string
	body: string
	x402Version: number
	selected: X402SelectedReq
	usdcDomain: { name: string; version: string }
	at: number
}

type CaddPrepCache = {
	account: string
	value: string
	spender: string
	nonce: string
	domain: { name: string; version: string }
	tokenAddress: Address
	at: number
}

type X402ClientMods = {
	selectPaymentRequirements: (reqs: unknown[], network: string, scheme: string) => unknown
	preparePaymentHeader: (
		from: string,
		version: number,
		selected: unknown,
	) => {
		x402Version: number
		scheme: string
		network: string
		payload: { authorization: Record<string, string> }
	}
	createPaymentHeader: (
		walletClient: unknown,
		version: number,
		selected: unknown,
	) => Promise<string>
	PaymentRequirementsSchema: { parse: (x: unknown) => unknown }
	decodeXPaymentResponse: (header: string) => unknown
}

let x402ClientModsPromise: Promise<X402ClientMods> | null = null
let x402ClientModsReady: X402ClientMods | null = null

function loadX402ClientMods(): Promise<X402ClientMods> {
	if (!x402ClientModsPromise) {
		x402ClientModsPromise = Promise.all([
			import('x402/client'),
			import('x402/types'),
			import('x402-fetch'),
		]).then(([client, types, xf]) => {
			const mods: X402ClientMods = {
				selectPaymentRequirements: client.selectPaymentRequirements,
				preparePaymentHeader: client.preparePaymentHeader,
				createPaymentHeader: client.createPaymentHeader,
				PaymentRequirementsSchema: types.PaymentRequirementsSchema,
				decodeXPaymentResponse: xf.decodeXPaymentResponse,
			}
			x402ClientModsReady = mods
			return mods
		})
	}
	return x402ClientModsPromise
}

function desktopHasCoinbaseExtension(wallets: InjectedWalletChoice[]): boolean {
	if (isMobileDeviceForWalletApps()) return false
	return wallets.some((w) => isDesktopCoinbaseWalletExtension(w, w.provider))
}

function asX402Selected(raw: unknown): X402SelectedReq | null {
	if (!raw || typeof raw !== 'object') return null
	const o = raw as Record<string, unknown>
	if (typeof o.maxAmountRequired !== 'string' || typeof o.asset !== 'string') return null
	return raw as X402SelectedReq
}

function paymentBusyStatus(status: Status): boolean {
	return (
		status === 'preparing-payment' ||
		status === 'awaiting-signature' ||
		status === 'settling' ||
		status === 'success'
	)
}

/** First await is `eth_signTypedData_v4`. Call only after a user gesture with no prior await. */
async function signCoinbaseUsdcX402Header(
	mods: X402ClientMods,
	provider: Eip1193Provider,
	account: Address,
	x402Version: number,
	selected: X402SelectedReq,
	usdcDomain: { name: string; version: string },
): Promise<string> {
	const unsigned = mods.preparePaymentHeader(account, x402Version, selected)
	const auth = unsigned.payload.authorization
	const signature = await withWalletTimeout(
		requestEthSignTypedDataV4(provider, account, {
			types: TRANSFER_WITH_AUTHORIZATION_TYPES,
			primaryType: 'TransferWithAuthorization',
			domain: {
				name: usdcDomain.name,
				version: usdcDomain.version,
				chainId: 8453,
				verifyingContract: getAddress(selected.asset),
			},
			message: {
				from: getAddress(auth.from),
				to: getAddress(auth.to),
				value: auth.value,
				validAfter: auth.validAfter,
				validBefore: auth.validBefore,
				nonce: auth.nonce,
			},
		}),
		WALLET_SIGNATURE_TIMEOUT_MS,
		'Wallet signature timed out. If the Coinbase popup stayed blank, close it and pay with MetaMask, or unlock Coinbase from the toolbar and tap Pay again.',
	)
	return encodeX402PaymentPayload({
		x402Version: unsigned.x402Version,
		scheme: unsigned.scheme,
		network: unsigned.network,
		payload: {
			signature,
			authorization: auth,
		},
	})
}

function formatCurrencyAmount(amount: string, currency: string): string {
	const n = Number(amount)
	if (!Number.isFinite(n)) return `${currency} ${amount}`
	const decimals = currency === 'JPY' || currency === 'TWD' ? 0 : 2
	return `${currency} ${n.toFixed(decimals)}`
}

function formatUsdc(usdc6OrHuman: string | undefined): string {
	if (!usdc6OrHuman) return '—'
	if (usdc6OrHuman.includes('.')) return `${Number(usdc6OrHuman).toFixed(2)} USDC`
	const n = Number(usdc6OrHuman)
	if (!Number.isFinite(n)) return '— USDC'
	return `${(n / 1_000_000).toFixed(2)} USDC`
}

const PAYER_USDC_REFRESH_MS = 8_000

function formatInsufficientUsdcError(have: number, need: number): string {
	return `Insufficient USDC balance: account has ${have.toFixed(2)} USDC, need ${need.toFixed(2)} USDC`
}

function isInsufficientUsdcError(msg: string | null | undefined): boolean {
	return typeof msg === 'string' && /Insufficient USDC balance/i.test(msg)
}

function decimalToAtomic6(raw: string): string | null {
	const s = String(raw ?? '').trim()
	if (!/^\d+(?:\.\d+)?$/.test(s)) return null
	const [intPart, fracPart = ''] = s.split('.')
	const frac6 = `${fracPart}000000`.slice(0, 6)
	try {
		return (BigInt(intPart) * 1_000_000n + BigInt(frac6)).toString()
	} catch {
		return null
	}
}

function tokenAddressBySymbol(symbol: 'USDC' | 'CADD'): Address {
	return symbol === 'CADD' ? BASE_CADD_ADDRESS : BASE_USDC_ADDRESS
}

async function resolveTokenDomain(symbol: 'USDC' | 'CADD', tokenAddress: Address): Promise<{ name: string; version: string }> {
	if (symbol === 'USDC') return { name: 'USD Coin', version: '2' }
	const pc = createPublicClient({
		chain: base,
		transport: http('https://base-rpc.conet.network'),
	})
	const abi = [
		{ type: 'function', name: 'name', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] },
		{ type: 'function', name: 'version', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] },
	] as const
	let name: string = symbol
	let version = '1'
	try {
		const n = await pc.readContract({ address: tokenAddress, abi, functionName: 'name' })
		if (typeof n === 'string' && n.trim()) name = n.trim()
	} catch {
		/* fallback */
	}
	try {
		const v = await pc.readContract({ address: tokenAddress, abi, functionName: 'version' })
		if (typeof v === 'string' && v.trim()) version = v.trim()
	} catch {
		/* fallback */
	}
	return { name, version }
}

function normalizeTo65ByteSignature(signature: string): string | null {
	if (!/^0x[0-9a-fA-F]+$/.test(signature)) return null
	const body = signature.slice(2)
	if (body.length === 130) return `0x${body}`
	if (body.length === 128) {
		const r = body.slice(0, 64)
		const vsHex = body.slice(64)
		let vs: bigint
		try {
			vs = BigInt(`0x${vsHex}`)
		} catch {
			return null
		}
		const highBitMask = 1n << 255n
		const sMask = highBitMask - 1n
		const v = (vs & highBitMask) !== 0n ? 28 : 27
		const s = (vs & sMask).toString(16).padStart(64, '0')
		const vHex = v.toString(16).padStart(2, '0')
		return `0x${r}${s}${vHex}`
	}
	return null
}

function isX402RequirementShapeError(errorMessage: string): boolean {
	return /maxAmountRequired|ZodError/i.test(errorMessage)
}

const NUMERIC_SPINNER_HIDE =
	'[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]'

function preventNumericInputStepKeys(e: KeyboardEvent<HTMLInputElement>): void {
	const blocked = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'])
	if (blocked.has(e.key)) {
		e.preventDefault()
		e.stopPropagation()
	}
}

function preventNumericInputWheelStep(e: WheelEvent<HTMLInputElement>): void {
	e.preventDefault()
	e.stopPropagation()
}

function normalizeUsdcAmountInput(raw: string): string {
	const n = Number(raw.trim())
	if (!Number.isFinite(n) || n <= 0) return ''
	const fixed = n.toFixed(2)
	return Number(fixed) >= 0.01 ? fixed : ''
}

export default function UsdcTopupPage() {
	const [sp] = useSearchParams()
	const parsed = useMemo(() => parseParams(sp), [sp])
	const [account, setAccount] = useState<Address | null>(null)
	const [chainIdHex, setChainIdHex] = useState<string | null>(null)
	const [quote, setQuote] = useState<QuoteResponse | null>(null)
	const [status, setStatus] = useState<Status>('idle')
	const [error, setError] = useState<string | null>(null)
	const [activeProvider, setActiveProvider] = useState<Eip1193Provider | null>(null)
	const [activeWalletChoice, setActiveWalletChoice] = useState<InjectedWalletChoice | null>(null)
	const [installedWallets, setInstalledWallets] = useState<InjectedWalletChoice[]>([])
	const [result, setResult] = useState<{
		usdcTx?: string
		topupTx?: string
		settle?: unknown
		/** POS QR：USDC 已结算，挂点mint 由终端 admin 离线签闭环 */
		awaitingPosAuthorization?: boolean
		/** POS 两阶段：仅 USDC 已付，顾客需在终端贴卡完成入账 */
		awaitingBeneficiaryTap?: boolean
	} | null>(null)
	const [depositAmountDraft, setDepositAmountDraft] = useState(
		() => (parsed.ok ? parsed.params.amount : ''),
	)
	const [payerUsdcBalance, setPayerUsdcBalance] = useState<number | null>(null)
	const statusRef = useRef<Status>('idle')
	const errorRef = useRef<string | null>(null)
	const accountRef = useRef<Address | null>(null)
	const paymentAttemptRef = useRef(0)
	const payerUsdcInFlightRef = useRef<Promise<number | null> | null>(null)
	const x402ChallengeCacheRef = useRef<X402ChallengeCache | null>(null)
	const caddPrepCacheRef = useRef<CaddPrepCache | null>(null)
	/** Coinbase extension: Pay only when 402 challenge + x402 mods are warm (user-gesture sign). */
	const [coinbaseX402Ready, setCoinbaseX402Ready] = useState(false)
	statusRef.current = status
	errorRef.current = error
	accountRef.current = account

	/** Only the wallet the user chose. Never fall back to window.ethereum (Phantom auto-login). */
	const eth = activeProvider

	const walletDepositAmount = useMemo(() => {
		if (!parsed.ok || parsed.params.workflow !== 'walletDeposit') {
			return parsed.ok ? parsed.params.amount : ''
		}
		return parsed.params.amount || normalizeUsdcAmountInput(depositAmountDraft)
	}, [parsed, depositAmountDraft])

	const needUsdcAmount = useMemo(() => {
		if (!parsed.ok || parsed.params.paymentToken !== 'USDC') return 0
		const raw = parsed.params.workflow === 'walletDeposit' ? walletDepositAmount : parsed.params.amount
		const n = Number(raw)
		return Number.isFinite(n) && n > 0 ? n : 0
	}, [parsed, walletDepositAmount])

	const refreshPayerUsdcBalance = useCallback(async (): Promise<number | null> => {
		if (!eth || !account || chainIdHex?.toLowerCase() !== BASE_CHAIN_ID_HEX) return null
		if (payerUsdcInFlightRef.current) return payerUsdcInFlightRef.current
		const requested = account
		const work = (async () => {
			const stillSameAccount = () =>
				(accountRef.current ?? '').toLowerCase() === requested.toLowerCase()
			// Never issue background eth_call requests through an injected wallet.
			// Coinbase Wallet serializes provider requests, so an unresolved balance
			// read can otherwise keep the user-initiated EIP-712 prompt queued.
			const balance = await fetchUsdcBalanceOnBase(requested)
			if (balance != null) {
				if (stillSameAccount()) setPayerUsdcBalance(balance)
				return balance
			}
			return null
		})()
		payerUsdcInFlightRef.current = work
		try {
			return await work
		} finally {
			if (payerUsdcInFlightRef.current === work) payerUsdcInFlightRef.current = null
		}
	}, [eth, account, chainIdHex])

	useEffect(() => {
		payerUsdcInFlightRef.current = null
		setPayerUsdcBalance(null)
	}, [account])

	const applyServerPayError = useCallback(async (serverError: string) => {
		if (isInsufficientUsdcError(serverError) && needUsdcAmount > 0) {
			const have = await refreshPayerUsdcBalance()
			if (have != null) {
				setError(
					have + 1e-9 >= needUsdcAmount
						? `Your wallet now has ${have.toFixed(2)} USDC on Base. Tap Pay again to continue.`
						: formatInsufficientUsdcError(have, needUsdcAmount),
				)
				setStatus('error')
				return
			}
		}
		setError(serverError)
		setStatus('error')
	}, [needUsdcAmount, refreshPayerUsdcBalance])

	useEffect(() => {
		if (!parsed.ok || typeof window === 'undefined') return
		// EIP-6963 discovery only. Do not read window.ethereum / window.phantom or
		// call eth_accounts until the user picks a wallet (Phantom auto-login).
		return subscribeInstalledInjectedWallets(setInstalledWallets)
	}, [parsed.ok])

	useEffect(() => {
		if (!activeProvider) return
		const onAccounts = (accs: unknown) => {
			const list = accs as string[] | undefined
			setAccount(list && list[0] ? (list[0] as Address) : null)
		}
		const onChain = (chain: unknown) => setChainIdHex(typeof chain === 'string' ? chain : null)
		activeProvider.on?.('accountsChanged', onAccounts as (...args: unknown[]) => void)
		activeProvider.on?.('chainChanged', onChain as (...args: unknown[]) => void)
		return () => {
			activeProvider.removeListener?.('accountsChanged', onAccounts as (...args: unknown[]) => void)
			activeProvider.removeListener?.('chainChanged', onChain as (...args: unknown[]) => void)
		}
	}, [activeProvider])

	useEffect(() => {
		if (!parsed.ok) return
		const { cardAddress, cardOwner, currency, workflow, beneficiary, packId } = parsed.params
		const amount =
			workflow === 'walletDeposit' ? walletDepositAmount : parsed.params.amount
		if (workflow === 'walletDeposit' && !amount) {
			setQuote(null)
			setStatus((s) => (s === 'quoting' ? 'idle' : s))
			return
		}
		setStatus((s) => (s === 'idle' ? 'quoting' : s))
		const quoteAmount = amount
		const url =
			workflow === 'walletDeposit'
				? `${BEAMIO_API}/api/nfcUsdcTopupQuote?workflow=walletDeposit&beneficiary=${encodeURIComponent(beneficiary)}&amount=${encodeURIComponent(quoteAmount)}&currency=USDC&paymentToken=USDC`
				: workflow === 'fuelPack'
					? `${BEAMIO_API}/api/nfcUsdcTopupQuote?workflow=fuelPack&beneficiary=${encodeURIComponent(beneficiary)}&amount=${encodeURIComponent(quoteAmount)}&currency=USDC&paymentToken=USDC${packId ? `&pack=${encodeURIComponent(packId)}` : ''}`
					: `${BEAMIO_API}/api/nfcUsdcTopupQuote?card=${cardAddress}&owner=${cardOwner}&amount=${encodeURIComponent(quoteAmount)}&currency=${currency}`
		let cancelled = false
		fetch(url)
			.then(async (r) => {
				const json = (await r.json().catch(() => ({}))) as QuoteResponse
				if (cancelled) return
				if (!r.ok || json.success === false) {
					setError(json.error ?? 'Failed to fetch quote')
					setStatus((s) => (s === 'quoting' || s === 'idle' ? 'error' : s))
					return
				}
				setQuote(json)
				// Never clobber an in-flight payment or success (mobile wallet return / remount races).
				setStatus((s) => (s === 'quoting' || s === 'idle' ? 'idle' : s))
			})
			.catch((e) => {
				if (cancelled) return
				setError(e?.message ?? String(e))
				setStatus((s) => (s === 'quoting' || s === 'idle' ? 'error' : s))
			})
		return () => {
			cancelled = true
		}
	}, [
		parsed.ok ? parsed.params.cardAddress : '',
		parsed.ok ? parsed.params.cardOwner : '',
		parsed.ok ? parsed.params.amount : '',
		parsed.ok ? parsed.params.currency : '',
		parsed.ok ? parsed.params.workflow : '',
		parsed.ok ? parsed.params.beneficiary : '',
		parsed.ok ? parsed.params.packId : '',
		walletDepositAmount,
	])

	useEffect(() => {
		if (!eth || !account || chainIdHex?.toLowerCase() !== BASE_CHAIN_ID_HEX) return
		let cancelled = false
		let timer: ReturnType<typeof setTimeout> | undefined

		const applyHave = (have: number) => {
			if (!isInsufficientUsdcError(errorRef.current)) return
			if (needUsdcAmount > 0 && have + 1e-9 >= needUsdcAmount) {
				setError(null)
				setStatus((s) => (s === 'error' ? 'idle' : s))
				return
			}
			if (needUsdcAmount > 0) {
				setError(formatInsufficientUsdcError(have, needUsdcAmount))
			}
		}

		const run = async () => {
			if (cancelled) return
			const busy =
				statusRef.current === 'preparing-payment' ||
				statusRef.current === 'awaiting-signature' ||
				statusRef.current === 'settling' ||
				statusRef.current === 'connecting' ||
				statusRef.current === 'quoting'
			if (busy || document.visibilityState !== 'visible') return
			const have = await refreshPayerUsdcBalance()
			if (cancelled || have == null) return
			applyHave(have)
		}

		const schedule = () => {
			timer = setTimeout(() => {
				void (async () => {
					if (cancelled || statusRef.current === 'success') return
					await run()
					if (!cancelled && statusRef.current !== 'success') schedule()
				})()
			}, PAYER_USDC_REFRESH_MS)
		}

		void run()
		schedule()
		const onVis = () => {
			if (document.visibilityState === 'visible') void run()
		}
		window.addEventListener('pageshow', onVis)
		document.addEventListener('visibilitychange', onVis)
		return () => {
			cancelled = true
			if (timer !== undefined) clearTimeout(timer)
			window.removeEventListener('pageshow', onVis)
			document.removeEventListener('visibilitychange', onVis)
		}
	}, [eth, account, chainIdHex, needUsdcAmount, refreshPayerUsdcBalance])

	useEffect(() => {
		if (!parsed.ok || parsed.params.paymentToken !== 'USDC') {
			setCoinbaseX402Ready(false)
			return
		}
		if (
			!desktopHasCoinbaseExtension(installedWallets) &&
			!isDesktopCoinbaseWalletExtension(activeWalletChoice, eth)
		) {
			setCoinbaseX402Ready(false)
			return
		}
		const payAmount =
			parsed.params.workflow === 'walletDeposit' ? walletDepositAmount : parsed.params.amount
		const built = buildUsdcNfcTopupBody(parsed.params, payAmount)
		if (!built.ok) {
			setCoinbaseX402Ready(false)
			return
		}
		const { body, x402MaxValue } = built
		let cancelled = false
		let timer: ReturnType<typeof setTimeout> | undefined

		const markReadyIfWarm = () => {
			const c = x402ChallengeCacheRef.current
			const warm =
				x402ClientModsReady != null &&
				c != null &&
				c.body === body &&
				Date.now() - c.at < X402_CHALLENGE_TTL_MS
			if (!cancelled) setCoinbaseX402Ready(warm)
		}

		const run = async () => {
			if (cancelled) return
			if (paymentBusyStatus(statusRef.current) || statusRef.current === 'success') return
			if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
			try {
				const mods = await loadX402ClientMods()
				if (cancelled) return
				const firstRes = await fetch(`${BEAMIO_API}/api/nfcUsdcTopup`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body,
				})
				if (cancelled || firstRes.status !== 402) {
					markReadyIfWarm()
					return
				}
				const challenge = (await firstRes.json()) as { x402Version: number; accepts: unknown[] }
				const parsedReqs = (challenge.accepts ?? []).map((x) =>
					mods.PaymentRequirementsSchema.parse(x),
				)
				const selected = asX402Selected(
					mods.selectPaymentRequirements(parsedReqs, 'base', 'exact'),
				)
				if (!selected) {
					markReadyIfWarm()
					return
				}
				if (BigInt(selected.maxAmountRequired) > x402MaxValue) {
					markReadyIfWarm()
					return
				}
				const extra = selected.extra ?? {}
				let usdcDomain = {
					name: extra.name || 'USD Coin',
					version: extra.version || '2',
				}
				if (!extra.name || !extra.version) {
					try {
						const d = await resolveTokenDomain('USDC', tokenAddressBySymbol('USDC'))
						usdcDomain = {
							name: extra.name || d.name,
							version: extra.version || d.version,
						}
					} catch {
						/* keep EIP-712 fallback — Pay must not await domain on click */
					}
				}
				if (cancelled) return
				x402ChallengeCacheRef.current = {
					key: body,
					body,
					x402Version: challenge.x402Version,
					selected,
					usdcDomain,
					at: Date.now(),
				}
				markReadyIfWarm()
			} catch {
				/* keep last trusted 402 challenge */
				markReadyIfWarm()
			}
		}

		const schedule = () => {
			timer = setTimeout(() => {
				void (async () => {
					if (cancelled) return
					await run()
					if (!cancelled) schedule()
				})()
			}, X402_PREFETCH_MS)
		}

		markReadyIfWarm()
		void run()
		schedule()
		return () => {
			cancelled = true
			if (timer !== undefined) clearTimeout(timer)
		}
	}, [
		parsed.ok,
		parsed.ok ? parsed.params.paymentToken : '',
		parsed.ok ? parsed.params.amount : '',
		parsed.ok ? parsed.params.currency : '',
		parsed.ok ? parsed.params.workflow : '',
		parsed.ok ? parsed.params.cardAddress : '',
		parsed.ok ? parsed.params.cardOwner : '',
		parsed.ok ? parsed.params.beneficiary : '',
		parsed.ok ? parsed.params.packId : '',
		parsed.ok ? parsed.params.aa : '',
		parsed.ok ? parsed.params.qty : 0,
		parsed.ok ? parsed.params.sid : '',
		parsed.ok ? parsed.params.pos : '',
		walletDepositAmount,
		installedWallets,
		activeWalletChoice,
		eth,
	])

	useEffect(() => {
		if (!parsed.ok || parsed.params.paymentToken !== 'CADD') return
		if (!account || !eth) return
		if (!isDesktopCoinbaseWalletExtension(activeWalletChoice, eth)) return
		const spender = (quote?.permitSpender ?? '').trim()
		if (!isEthAddress(spender)) return
		const value = caddPermitValueFromQuote(parsed.params, quote)
		if (!value) return
		const tokenAddress = tokenAddressBySymbol('CADD')
		let cancelled = false
		let timer: ReturnType<typeof setTimeout> | undefined

		const run = async () => {
			if (cancelled) return
			if (paymentBusyStatus(statusRef.current) || statusRef.current === 'success') return
			if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
			try {
				const domain = await resolveTokenDomain('CADD', tokenAddress)
				const pc = createPublicClient({
					chain: base,
					transport: http('https://base-rpc.conet.network'),
				})
				const permitNonceOnChain = await pc.readContract({
					address: tokenAddress,
					abi: [
						{
							type: 'function',
							name: 'nonces',
							stateMutability: 'view',
							inputs: [{ name: 'owner', type: 'address' }],
							outputs: [{ type: 'uint256' }],
						},
					] as const,
					functionName: 'nonces',
					args: [account],
				})
				if (cancelled) return
				caddPrepCacheRef.current = {
					account: account.toLowerCase(),
					value,
					spender: spender.toLowerCase(),
					nonce: permitNonceOnChain.toString(),
					domain,
					tokenAddress,
					at: Date.now(),
				}
			} catch {
				/* keep last trusted CADD nonce/domain */
			}
		}

		const schedule = () => {
			timer = setTimeout(() => {
				void (async () => {
					if (cancelled) return
					await run()
					if (!cancelled) schedule()
				})()
			}, X402_PREFETCH_MS)
		}

		void run()
		schedule()
		return () => {
			cancelled = true
			if (timer !== undefined) clearTimeout(timer)
		}
	}, [
		parsed.ok,
		parsed.ok ? parsed.params.paymentToken : '',
		parsed.ok ? parsed.params.amount : '',
		parsed.ok ? parsed.params.currency : '',
		account,
		eth,
		activeWalletChoice,
		quote,
	])

	const connectWallet = async (choice: InjectedWalletChoice) => {
		const raw = choice.provider
		if (!raw) return
		const useCoinbase = isDesktopCoinbaseWalletExtension(choice, raw)
		const provider = useCoinbase ? wrapCoinbaseExtensionProvider(raw) : raw
		if (useCoinbase) {
			void loadX402ClientMods()
		}
		setError(null)
		setActiveWalletChoice(choice)
		setActiveProvider(provider)
		/*
		 * Coinbase extension: first await after the click must be eth_requestAccounts.
		 * setStatus('connecting') before request is fine (sync), but never await fetch/import first.
		 */
		const accountsPromise = provider.request({ method: 'eth_requestAccounts' })
		setStatus('connecting')
		try {
			const accounts = (await withWalletTimeout(
				accountsPromise,
				WALLET_REQUEST_TIMEOUT_MS,
				'Wallet connection timed out. Close any stuck Coinbase popup (Initializing / portfolio), open the extension, approve the connection, then try again.',
			)) as string[]
			if (!accounts?.[0] || !isEthAddress(accounts[0])) {
				throw new Error('Wallet did not provide an account.')
			}
			setAccount(accounts[0] as Address)
			const chain = (await withWalletTimeout(
				provider.request({ method: 'eth_chainId' }),
				WALLET_REQUEST_TIMEOUT_MS,
				'Wallet network request timed out. Open the wallet extension and try again.',
			)) as string
			setChainIdHex(chain)
			setStatus('idle')
		} catch (e: unknown) {
			const err = e as { name?: string; message?: string; cause?: unknown; code?: number | string } | null
			const name = err?.name ?? typeof e
			const msg = err?.message ?? String(e)
			const codeStr = err?.code !== undefined ? ` code=${err.code}` : ''
			const causeStr = err?.cause ? ` cause=${err.cause instanceof Error ? err.cause.message : String(err.cause)}` : ''
			if (isX402RequirementShapeError(msg)) {
				setError(
					`Payment requirement schema mismatch from server (x402 maxAmountRequired). Please retry in a moment.${codeStr}${causeStr}`
				)
				setStatus('error')
				return
			}
			setError(`${name}: ${msg}${codeStr}${causeStr}`)
			setStatus('error')
		}
	}

	const switchToBase = async () => {
		if (!eth) return
		setError(null)
		setStatus('switching-chain')
		try {
			await eth.request({
				method: 'wallet_switchEthereumChain',
				params: [{ chainId: BASE_CHAIN_ID_HEX }],
			})
			setChainIdHex(BASE_CHAIN_ID_HEX)
			setStatus('idle')
		} catch (e: unknown) {
			const err = e as { code?: number; message?: string }
			if (err?.code === 4902) {
				try {
					await eth.request({
						method: 'wallet_addEthereumChain',
						params: [
							{
								chainId: BASE_CHAIN_ID_HEX,
								chainName: 'Base',
								nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
								rpcUrls: ['https://mainnet.base.org'],
								blockExplorerUrls: ['https://basescan.org'],
							},
						],
					})
					setChainIdHex(BASE_CHAIN_ID_HEX)
					setStatus('idle')
					return
				} catch (addErr: unknown) {
					const aMsg = addErr instanceof Error ? addErr.message : String(addErr)
					setError(aMsg)
					setStatus('error')
					return
				}
			}
			setError(err?.message ?? 'Failed to switch chain')
			setStatus('error')
		}
	}

	const payWithUsdc = async () => {
		if (!parsed.ok || !eth || !account) return
		const paymentAttempt = ++paymentAttemptRef.current
		setError(null)
		setResult(null)
		// Do not set preparing-payment here. Coinbase's desktop extension drops
		// Chrome user activation if we await fetch/import before eth_signTypedData_v4.
		// Cache-hit: first await must be the signature. Cache-miss: prepare, then sign.
		const useCoinbaseExt = isDesktopCoinbaseWalletExtension(activeWalletChoice, eth)
		try {
			if (parsed.params.paymentToken === 'CADD') {
				const value = caddPermitValueFromQuote(parsed.params, quote)
				if (!value) {
					setError('Missing CADD quote amount.')
					setStatus('error')
					return
				}
				const permitDeadline = String(Math.floor(Date.now() / 1000) + 120)
				const tokenAddress = tokenAddressBySymbol('CADD')
				const spender = (quote?.permitSpender ?? '').trim()
				if (!isEthAddress(spender)) {
					setError('CADD permit spender is missing from quote response.')
					setStatus('error')
					return
				}
				const cachedCadd = caddPrepCacheRef.current
				const caddCacheHit =
					useCoinbaseExt &&
					cachedCadd != null &&
					Date.now() - cachedCadd.at < CADD_PREP_TTL_MS &&
					cachedCadd.account === account.toLowerCase() &&
					cachedCadd.value === value &&
					cachedCadd.spender === spender.toLowerCase()
				let domain: { name: string; version: string }
				let permitNonce: string
				if (caddCacheHit && cachedCadd) {
					caddPrepCacheRef.current = null
					domain = cachedCadd.domain
					permitNonce = cachedCadd.nonce
				} else {
					setStatus('preparing-payment')
					domain = await resolveTokenDomain('CADD', tokenAddress)
					const pc = createPublicClient({
						chain: base,
						transport: http('https://base-rpc.conet.network'),
					})
					const permitNonceOnChain = await pc.readContract({
						address: tokenAddress,
						abi: [{ type: 'function', name: 'nonces', stateMutability: 'view', inputs: [{ name: 'owner', type: 'address' }], outputs: [{ type: 'uint256' }] }] as const,
						functionName: 'nonces',
						args: [account],
					})
					permitNonce = permitNonceOnChain.toString()
				}
				setStatus('awaiting-signature')
				const signatureRaw = await withWalletTimeout(
					requestEthSignTypedDataV4(eth, account, {
						types: {
							Permit: [
								{ name: 'owner', type: 'address' },
								{ name: 'spender', type: 'address' },
								{ name: 'value', type: 'uint256' },
								{ name: 'nonce', type: 'uint256' },
								{ name: 'deadline', type: 'uint256' },
							],
						},
						primaryType: 'Permit',
						domain: {
							name: domain.name,
							version: domain.version,
							chainId: 8453,
							verifyingContract: tokenAddress,
						},
						message: {
							owner: account,
							spender,
							value,
							nonce: permitNonce,
							deadline: permitDeadline,
						},
					}),
					WALLET_SIGNATURE_TIMEOUT_MS,
					useCoinbaseExt
						? 'Wallet signature timed out. Open the Coinbase Wallet extension and approve the request, then try again.'
						: 'Wallet signature timed out. Open the wallet extension and approve the request, then try again.',
				)
				if (paymentAttempt !== paymentAttemptRef.current) return
				const signature = normalizeTo65ByteSignature(signatureRaw)
				if (!signature) {
					setError('Wallet returned unsupported signature format.')
					setStatus('error')
					return
				}
				const p = parsed.params
				const bodyObj: Record<string, string> = {
					cardAddress: p.cardAddress,
					cardOwner: p.cardOwner,
					amount: p.amount,
					currency: p.currency,
					paymentToken: 'CADD',
					payer: account,
					value,
					permitDeadline,
					permitNonce,
					signature,
				}
				if (p.workflow === 'treasuryBridge' && p.aa) {
					bodyObj.aa = p.aa
					bodyObj.workflow = 'treasuryBridge'
				} else if (p.workflow === 'genesisNodeSeat' && p.beneficiary) {
					bodyObj.beneficiary = p.beneficiary
					bodyObj.qty = String(p.qty)
					bodyObj.workflow = 'genesisNodeSeat'
					if (p.referrerL0) bodyObj.referrerL0 = p.referrerL0
				} else if (p.workflow === 'walletDeposit' && p.beneficiary) {
					bodyObj.beneficiary = p.beneficiary
					bodyObj.workflow = 'walletDeposit'
					delete bodyObj.cardAddress
					delete bodyObj.cardOwner
				} else if (p.workflow === 'fuelPack' && p.beneficiary) {
					bodyObj.beneficiary = p.beneficiary
					bodyObj.workflow = 'fuelPack'
					if (p.packId) bodyObj.pack = p.packId
					delete bodyObj.cardAddress
					delete bodyObj.cardOwner
				} else if (p.workflow === 'clientTopup' && p.beneficiary) {
					bodyObj.beneficiary = p.beneficiary
					bodyObj.workflow = 'clientTopup'
				} else {
					if (p.sid) bodyObj.sid = p.sid
					if (p.pos) bodyObj.pos = p.pos
					if (p.uid) {
						bodyObj.uid = p.uid
						bodyObj.e = p.e
						bodyObj.c = p.c
						bodyObj.m = p.m
					}
				}
				applyMembershipPayFieldsToBody(bodyObj, p)
				setStatus('settling')
				const response = await fetch(`${BEAMIO_API}/api/nfcUsdcTopup`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(bodyObj),
				})
				const json = (await response.json().catch(() => ({}))) as {
					success?: boolean
					error?: string
					USDC_tx?: string
					executeForAdmin_tx?: string
					claimTx?: string
					awaitingPosAuthorization?: boolean
					awaitingBeneficiaryTap?: boolean
				}
				if (!response.ok || json.success === false) {
					await applyServerPayError(json.error ?? `Topup failed (HTTP ${response.status})`)
					return
				}
				setResult({
					usdcTx: json.USDC_tx,
					topupTx: json.executeForAdmin_tx ?? json.claimTx,
					awaitingPosAuthorization: json.awaitingPosAuthorization === true,
					awaitingBeneficiaryTap: json.awaitingBeneficiaryTap === true,
				})
				setStatus('success')
				return
			}
			const p = parsed.params
			const payAmount = p.workflow === 'walletDeposit' ? walletDepositAmount : p.amount
			const built = buildUsdcNfcTopupBody(p, payAmount)
			if (!built.ok) {
				setError(built.error)
				setStatus('error')
				return
			}
			const { body, x402MaxValue } = built
			const topupUrl = `${BEAMIO_API}/api/nfcUsdcTopup`
			const modsSync = x402ClientModsReady
			const cachedX402 = x402ChallengeCacheRef.current
			const x402CacheHit =
				useCoinbaseExt &&
				modsSync != null &&
				cachedX402 != null &&
				Date.now() - cachedX402.at < X402_CHALLENGE_TTL_MS &&
				cachedX402.body === body
			let paymentHeader: string
			let decodeXPaymentResponse = modsSync?.decodeXPaymentResponse

			const cacheWarmChallenge = async (
				mods: X402ClientMods,
				challenge: { x402Version: number; accepts: unknown[] },
				selected: X402SelectedReq,
			) => {
				const extra = selected.extra ?? {}
				let usdcDomain = {
					name: extra.name || 'USD Coin',
					version: extra.version || '2',
				}
				if (!extra.name || !extra.version) {
					try {
						const d = await resolveTokenDomain('USDC', tokenAddressBySymbol('USDC'))
						usdcDomain = {
							name: extra.name || d.name,
							version: extra.version || d.version,
						}
					} catch {
						/* EIP-712 fallback names are fine for Pay click */
					}
				}
				x402ChallengeCacheRef.current = {
					key: body,
					body,
					x402Version: challenge.x402Version,
					selected,
					usdcDomain,
					at: Date.now(),
				}
				setCoinbaseX402Ready(true)
			}

			if (useCoinbaseExt && x402CacheHit && cachedX402 && modsSync) {
				/*
				 * Coinbase: first await after click MUST be eth_signTypedData_v4.
				 * Start the sign promise before setStatus so React paint cannot run first.
				 */
				const signPromise = signCoinbaseUsdcX402Header(
					modsSync,
					eth,
					account,
					cachedX402.x402Version,
					cachedX402.selected,
					cachedX402.usdcDomain,
				)
				setStatus('awaiting-signature')
				paymentHeader = await signPromise
			} else if (useCoinbaseExt) {
				/*
				 * Never open the extension after awaiting fetch/import — gesture is lost and Coinbase
				 * stays on the startup loading screen. Warm 402 + mods, then ask for a second tap.
				 */
				setStatus('preparing-payment')
				setCoinbaseX402Ready(false)
				const mods = modsSync ?? (await loadX402ClientMods())
				decodeXPaymentResponse = mods.decodeXPaymentResponse
				const firstRes = await withWalletTimeout(
					fetch(topupUrl, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body,
					}),
					WALLET_REQUEST_TIMEOUT_MS,
					'Payment preparation timed out. Check your connection and try again.',
				)
				if (paymentAttempt !== paymentAttemptRef.current) return
				if (firstRes.status !== 402) {
					setStatus('settling')
					const json = (await firstRes.json().catch(() => ({}))) as {
						success?: boolean
						error?: string
						USDC_tx?: string
						executeForAdmin_tx?: string
						claimTx?: string
						awaitingPosAuthorization?: boolean
						awaitingBeneficiaryTap?: boolean
					}
					if (!firstRes.ok || json.success === false) {
						await applyServerPayError(json.error ?? `Topup failed (HTTP ${firstRes.status})`)
						return
					}
					setResult({
						usdcTx: json.USDC_tx,
						topupTx: json.executeForAdmin_tx ?? json.claimTx,
						awaitingPosAuthorization: json.awaitingPosAuthorization === true,
						awaitingBeneficiaryTap: json.awaitingBeneficiaryTap === true,
					})
					setStatus('success')
					return
				}
				const challenge = (await firstRes.json()) as { x402Version: number; accepts: unknown[] }
				const parsedReqs = (challenge.accepts ?? []).map((x) =>
					mods.PaymentRequirementsSchema.parse(x),
				)
				const selected = asX402Selected(
					mods.selectPaymentRequirements(parsedReqs, 'base', 'exact'),
				)
				if (!selected) {
					setError('No compatible payment requirement from server.')
					setStatus('error')
					return
				}
				if (BigInt(selected.maxAmountRequired) > x402MaxValue) {
					setError('Payment amount exceeds maximum allowed. Hard-refresh this page and try again.')
					setStatus('error')
					return
				}
				await cacheWarmChallenge(mods, challenge, selected)
				if (paymentAttempt !== paymentAttemptRef.current) return
				setError(null)
				setStatus('idle')
				return
			} else {
				setStatus('preparing-payment')
				const mods = modsSync ?? (await loadX402ClientMods())
				decodeXPaymentResponse = mods.decodeXPaymentResponse
				const firstRes = await withWalletTimeout(
					fetch(topupUrl, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body,
					}),
					WALLET_REQUEST_TIMEOUT_MS,
					'Payment preparation timed out. Check your connection and try again.',
				)
				if (firstRes.status !== 402) {
					setStatus('settling')
					const json = (await firstRes.json().catch(() => ({}))) as {
						success?: boolean
						error?: string
						USDC_tx?: string
						executeForAdmin_tx?: string
						claimTx?: string
						awaitingPosAuthorization?: boolean
						awaitingBeneficiaryTap?: boolean
					}
					if (!firstRes.ok || json.success === false) {
						await applyServerPayError(json.error ?? `Topup failed (HTTP ${firstRes.status})`)
						return
					}
					setResult({
						usdcTx: json.USDC_tx,
						topupTx: json.executeForAdmin_tx ?? json.claimTx,
						awaitingPosAuthorization: json.awaitingPosAuthorization === true,
						awaitingBeneficiaryTap: json.awaitingBeneficiaryTap === true,
					})
					setStatus('success')
					return
				}
				const challenge = (await firstRes.json()) as { x402Version: number; accepts: unknown[] }
				const parsedReqs = (challenge.accepts ?? []).map((x) =>
					mods.PaymentRequirementsSchema.parse(x),
				)
				const selected = asX402Selected(
					mods.selectPaymentRequirements(parsedReqs, 'base', 'exact'),
				)
				if (!selected) {
					setError('No compatible payment requirement from server.')
					setStatus('error')
					return
				}
				if (BigInt(selected.maxAmountRequired) > x402MaxValue) {
					setError('Payment amount exceeds maximum allowed. Hard-refresh this page and try again.')
					setStatus('error')
					return
				}
				setStatus('awaiting-signature')
				const walletClient = createWalletClient({
					account,
					chain: base,
					transport: custom(eth),
				})
				paymentHeader = await withWalletTimeout(
					mods.createPaymentHeader(walletClient, challenge.x402Version, selected),
					WALLET_SIGNATURE_TIMEOUT_MS,
					'Wallet signature timed out. Open the wallet extension and approve the request, then try again.',
				)
			}
			if (paymentAttempt !== paymentAttemptRef.current) return
			setStatus('settling')
			const response = await withWalletTimeout(
				fetch(topupUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-PAYMENT': paymentHeader,
						'Access-Control-Expose-Headers': 'X-PAYMENT-RESPONSE',
					},
					body,
				}),
				WALLET_REQUEST_TIMEOUT_MS,
				'Payment confirmation timed out. Check your connection and try again.',
			)
			if (paymentAttempt !== paymentAttemptRef.current) return
			const json = (await response.json().catch(() => ({}))) as {
				success?: boolean
				error?: string
				USDC_tx?: string
				executeForAdmin_tx?: string
				claimTx?: string
				awaitingPosAuthorization?: boolean
				awaitingBeneficiaryTap?: boolean
			}
			let decoded: unknown = null
			try {
				const xPayResp = response.headers.get('x-payment-response')
				decoded =
					xPayResp && decodeXPaymentResponse ? decodeXPaymentResponse(xPayResp) : null
			} catch {
				decoded = null
			}
			if (!response.ok || json.success === false) {
				await applyServerPayError(json.error ?? `Topup failed (HTTP ${response.status})`)
				return
			}
			x402ChallengeCacheRef.current = null
			setCoinbaseX402Ready(false)
			setResult({
				usdcTx: json.USDC_tx,
				topupTx: json.executeForAdmin_tx ?? json.claimTx,
				settle: decoded,
				awaitingPosAuthorization: json.awaitingPosAuthorization === true,
				awaitingBeneficiaryTap: json.awaitingBeneficiaryTap === true,
			})
			setStatus('success')
		} catch (e: unknown) {
			if (paymentAttempt !== paymentAttemptRef.current) return
			const msg = e instanceof Error ? e.message : String(e)
			setError(msg)
			setStatus('error')
		}
	}

	const cancelPaymentWait = () => {
		paymentAttemptRef.current += 1
		setError('Payment request cancelled. You can try again when your wallet is ready.')
		setStatus('error')
	}

	const cancelWalletConnect = () => {
		setError(
			'Wallet connection cancelled. If Coinbase is stuck on Initializing, close that popup and tap Connect again. (Coinbase analytics errors like as.coinbase.com/amp 400 are unrelated.)',
		)
		setStatus('error')
		setAccount(null)
		setActiveProvider(null)
		setActiveWalletChoice(null)
	}

	/** Coinbase extension ports die if the page hits bfcache while awaiting signature. */
	useEffect(() => {
		if (status !== 'awaiting-signature') return
		const onBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault()
			e.returnValue = ''
		}
		const onPageShow = (e: PageTransitionEvent) => {
			if (!e.persisted) return
			paymentAttemptRef.current += 1
			setError('Wallet connection was interrupted. Tap Pay again to continue.')
			setStatus('error')
		}
		window.addEventListener('beforeunload', onBeforeUnload)
		window.addEventListener('pageshow', onPageShow)
		return () => {
			window.removeEventListener('beforeunload', onBeforeUnload)
			window.removeEventListener('pageshow', onPageShow)
		}
	}, [status])

	if (!parsed.ok) {
		return (
			<div className="min-h-dvh bg-background text-on-surface antialiased">
				<UsdcTopupSiteHeader />
				<main className="pt-24 pb-12">
					<div className="mx-auto max-w-xl px-6">
						<div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-200">
							<h2 className="mb-2 text-xl font-bold">Invalid topup link</h2>
							<p className="text-sm leading-relaxed">{parsed.error}</p>
							<p className="mt-4 text-xs opacity-80">
								Expected: <code>card</code>, <code>owner</code>, <code>amount</code>, <code>currency</code>; Discover{' '}
								<code>workflow=treasuryBridge</code> + <code>aa</code>;{' '}
								<code>workflow=genesisNodeSeat</code> + <code>beneficiary</code> + <code>qty</code>; wallet deposit{' '}
								<code>workflow=walletDeposit</code> + <code>beneficiary</code> (no card); Custom Fuel{' '}
								<code>workflow=fuelPack</code> + <code>beneficiary</code> + <code>pack</code> (no card); POS session{' '}
								<code>sid</code> + admin <code>pos</code>; or legacy <code>workflow=clientTopup</code> +{' '}
								<code>beneficiary</code>. Legacy NFC links need full <code>uid</code>/<code>e</code>/<code>c</code>/
								<code>m</code>.
							</p>
						</div>
					</div>
				</main>
			</div>
		)
	}

	const { cardAddress, cardOwner, uid, amount, currency, sid: topupSid, beneficiary: topupBeneficiary, aa: topupAa, qty: topupQty, workflow: topupWorkflow, packId: topupPackId } =
		parsed.params
	const isTreasuryBridge = topupWorkflow === 'treasuryBridge' && Boolean(topupAa)
	const isClientTopup = topupWorkflow === 'clientTopup' && Boolean(topupBeneficiary)
	const isGenesisSeat = topupWorkflow === 'genesisNodeSeat' && Boolean(topupBeneficiary) && topupQty > 0
	const isWalletDeposit = topupWorkflow === 'walletDeposit' && Boolean(topupBeneficiary)
	const isFuelPack = topupWorkflow === 'fuelPack' && Boolean(topupBeneficiary)
	const fuelPackEntry = isFuelPack ? lookupFuelPack(topupPackId) : null
	const showNfcTagRow = Boolean(uid && uid.length >= 6)
	const onBase = chainIdHex?.toLowerCase() === BASE_CHAIN_ID_HEX
	const hasInjectedWallet = installedWallets.length > 0
	const ready = !!activeProvider && !!account && onBase
	const coinbaseExt = isDesktopCoinbaseWalletExtension(activeWalletChoice, eth)

	const quotedUsdcLabel = formatUsdc(quote?.quotedUsdc ?? quote?.quotedUsdc6).replace(/USDC/g, parsed.params.paymentToken)

	return (
		<div className="min-h-dvh bg-background text-on-surface antialiased">
			<UsdcTopupSiteHeader />
			{status === 'connecting' && (
				<div
					className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 bg-[#f9f9fe]/95 px-6 text-center backdrop-blur-sm"
					role="status"
					aria-live="polite"
					aria-busy="true"
				>
					<div
						className="h-10 w-10 animate-spin rounded-full border-[3px] border-blue-600/20 border-t-blue-600"
						aria-hidden
					/>
					<p className="text-lg font-bold text-[#1a1c1f]">
						{coinbaseExt || activeWalletChoice?.id === 'coinbase' || activeWalletChoice?.id === 'base'
							? 'Approve in Coinbase Wallet extension…'
							: 'Connecting wallet…'}
					</p>
					<p className="max-w-sm text-sm text-slate-500">
						Open the wallet extension popup in this browser and approve the connection. If Coinbase
						stays on Initializing / portfolio loading, close that window and try Connect again.
					</p>
					<button
						type="button"
						onClick={cancelWalletConnect}
						className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
					>
						Cancel
					</button>
				</div>
			)}
			{(status === 'preparing-payment' ||
				status === 'awaiting-signature' ||
				status === 'settling') && (
				<div
					className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 bg-[#f9f9fe]/95 px-6 text-center backdrop-blur-sm"
					role="status"
					aria-live="polite"
					aria-busy="true"
				>
					<div
						className="h-10 w-10 animate-spin rounded-full border-[3px] border-blue-600/20 border-t-blue-600"
						aria-hidden
					/>
					<p className="text-lg font-bold text-[#1a1c1f]">
						{status === 'preparing-payment'
							? 'Preparing payment…'
							: status === 'awaiting-signature'
								? coinbaseExt
									? 'Approve in Coinbase Wallet extension…'
									: 'Waiting for wallet signature…'
								: 'Confirming payment…'}
					</p>
					<p className="max-w-sm text-sm text-slate-500">
						{status === 'preparing-payment'
							? 'Contacting the payment server. This usually takes a few seconds.'
							: status === 'awaiting-signature'
								? coinbaseExt
									? 'Approve USDC TransferWithAuthorization in the Coinbase popup. If that window stays white with only a spinner, close it, Cancel here, then Connect with MetaMask and pay again.'
									: 'Approve the payment in your browser wallet extension popup. This page will update when you confirm.'
								: 'Settling on Base. Keep this page open.'}
					</p>
					{status === 'preparing-payment' || status === 'awaiting-signature' ? (
						<button
							type="button"
							onClick={cancelPaymentWait}
							className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
						>
							Cancel
						</button>
					) : null}
				</div>
			)}
			<main className="pt-24 pb-12">
				<div className="mx-auto max-w-xl px-6">
					<header className="mb-8 text-center">
						<h1 className="text-3xl font-extrabold tracking-tight">
							{isFuelPack
								? 'Buy Fuel Pack'
								: isWalletDeposit
									? 'Deposit USDC'
									: isGenesisSeat
										? 'Lock Genesis Node Seat'
										: 'Top up your card'}
						</h1>
						<p className="mt-2 text-on-surface-variant">
							Pay with {parsed.params.paymentToken} on Base from your own wallet.
							{isFuelPack
								? ' After payment confirms, B-Units (and a Genesis merchant-card NFT if this is the Newcomer pack) credit the merchant account — not the paying wallet.'
								: isWalletDeposit
								? ' After payment confirms, CoNET-USDC is minted to your wallet via the Base–CoNET treasury bridge.'
								: isGenesisSeat
									? ' After payment confirms, your validator nodes are claimed and deployed automatically.'
									: isTreasuryBridge
										? ' USDC settles to the Beamio treasury; card points credit to your Smart Wallet. The merchant receives CoNET-USDC separately.'
										: isClientTopup
											? ' USDC is sent to the beneficiary wallet; complete the merchant top-up in the Beamio app.'
											: topupSid
												? ' After payment, tap your Beamio card on the merchant terminal to receive the credit.'
												: ' Your NFC card will be credited automatically.'}
						</p>
					</header>

					<section className="rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
						<div className="grid grid-cols-1 gap-3 text-sm">
							{isFuelPack ? (
								<>
									{fuelPackEntry ? (
										<Row label="Pack" value={fuelPackEntry.name} mono={false} bold />
									) : null}
									<Row
										label="You pay"
										value={status === 'quoting' ? 'Quoting…' : quotedUsdcLabel}
										mono
										bold
									/>
									{fuelPackEntry ? (
										<Row
											label="Credits"
											/* Total only — never render Paid/Free split (beamio-fuel-pack-display-no-paid-free). */
											value={`${(fuelPackEntry.paidBUnits + fuelPackEntry.freeBUnits).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} B-Units${fuelPackEntry.firstTimeOnly ? ' + Genesis merchant-card NFT' : ''}`}
											mono={false}
										/>
									) : null}
									<Divider />
									<Row label="Credit to (merchant)" value={truncate(topupBeneficiary, 8, 6)} mono />
								</>
							) : isWalletDeposit ? (
								<>
									{Number(parsed.params.amount) > 0 ? (
										<Row
											label="Deposit amount"
											value={formatCurrencyAmount(walletDepositAmount || amount, currency)}
											mono={false}
											bold
										/>
									) : (
										<div>
											<label
												htmlFor="wallet-deposit-usdc-amount"
												className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant"
											>
												Deposit amount (USDC)
											</label>
											<input
												id="wallet-deposit-usdc-amount"
												type="number"
												inputMode="decimal"
												autoComplete="off"
												enterKeyHint="done"
												min={0.01}
												step={0.01}
												value={depositAmountDraft}
												onChange={(e) => {
													setDepositAmountDraft(e.target.value)
													if (error) setError(null)
												}}
												onKeyDown={preventNumericInputStepKeys}
												onWheel={preventNumericInputWheelStep}
												placeholder="10.00"
												className={`mt-2 w-full rounded-2xl border border-outline-variant/30 bg-white px-4 py-3 text-base font-semibold text-[#1a1c1f] outline-none focus:ring-2 focus:ring-blue-200 ${NUMERIC_SPINNER_HIDE}`}
											/>
										</div>
									)}
									<Row
										label="You pay"
										value={
											!walletDepositAmount
												? 'Enter amount'
												: status === 'quoting'
													? 'Quoting…'
													: quotedUsdcLabel
										}
										mono
										bold
									/>
									<Divider />
									<Row label="Credit to (CoNET-USDC)" value={truncate(topupBeneficiary, 8, 6)} mono />
								</>
							) : isGenesisSeat ? (
								<>
									<Row
										label="Nodes"
										value={`${topupQty} Genesis Node${topupQty > 1 ? 's' : ''} (Cloud Included)`}
										mono={false}
										bold
									/>
									<Row label="You pay" value={status === 'quoting' ? 'Quoting…' : quotedUsdcLabel} mono bold />
									<Divider />
									<Row label="Buyer (beneficiary)" value={truncate(topupBeneficiary, 8, 6)} mono />
									<Row label="Program card" value={truncate(cardAddress, 8, 6)} mono />
								</>
							) : (
								<>
									<Row label="Top-up amount" value={formatCurrencyAmount(amount, currency)} mono={false} bold />
									<Row label="You pay" value={status === 'quoting' ? 'Quoting…' : quotedUsdcLabel} mono bold />
									<Divider />
									<Row label="Merchant (card owner)" value={truncate(cardOwner, 8, 6)} mono />
									<Row label="BeamioUserCard" value={truncate(cardAddress, 8, 6)} mono />
									{isTreasuryBridge ? (
										<Row label="Smart Wallet (AA)" value={truncate(topupAa, 8, 6)} mono />
									) : isClientTopup ? (
										<Row label="Beneficiary wallet" value={truncate(topupBeneficiary, 8, 6)} mono />
									) : showNfcTagRow ? (
										<Row label="NFC tag" value={`…${uid.slice(-6).toUpperCase()}`} mono />
									) : (
										<Row label="NFC tag" value="After payment — tap card at terminal" mono={false} />
									)}
								</>
							)}
							<Row label="Network" value="Base mainnet" mono={false} />
						</div>
					</section>

					<section className="mt-6">
						{!hasInjectedWallet ? (
							isMobileDeviceForWalletApps() ? (
								<MobileWalletPayPanel />
							) : (
								<NoWalletPanel />
							)
						) : !account ? (
							<InstalledInjectedWalletPicker
								wallets={installedWallets}
								connecting={status === 'connecting'}
								onSelect={(w) => void connectWallet(w)}
							/>
						) : !onBase ? (
							<button
								type="button"
								onClick={switchToBase}
								disabled={status === 'switching-chain'}
								className="w-full rounded-full bg-amber-500 px-8 py-4 text-lg font-bold text-white shadow-lg transition-all hover:bg-amber-400 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
							>
								{status === 'switching-chain' ? 'Switching…' : 'Switch to Base'}
							</button>
						) : status === 'success' ? (
							<SuccessPanel
								usdcTx={result?.usdcTx}
								topupTx={result?.topupTx}
								tokenLabel={parsed.params.paymentToken}
								awaitingPosAuthorization={result?.awaitingPosAuthorization}
								awaitingBeneficiaryTap={result?.awaitingBeneficiaryTap}
								treasuryBridge={isTreasuryBridge}
								genesisSeat={isGenesisSeat}
								walletDeposit={isWalletDeposit}
								fuelPack={isFuelPack}
								onDone={() => window.close()}
							/>
						) : (
							<button
								type="button"
								onClick={payWithUsdc}
								disabled={
									status === 'awaiting-signature' ||
									status === 'settling' ||
									status === 'preparing-payment' ||
									status === 'quoting' ||
									!quote ||
									(isWalletDeposit && !walletDepositAmount) ||
									(coinbaseExt &&
										parsed.params.paymentToken === 'USDC' &&
										!coinbaseX402Ready)
								}
								className="w-full rounded-full bg-blue-600 px-8 py-4 text-lg font-bold text-white shadow-lg transition-all hover:bg-blue-500 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
							>
								{status === 'preparing-payment' && 'Preparing payment…'}
								{status === 'awaiting-signature' &&
									(coinbaseExt ? 'Waiting for extension…' : 'Waiting for wallet signature…')}
								{status === 'settling' && 'Settling on-chain…'}
								{status === 'quoting' && 'Loading quote…'}
								{(status === 'idle' || status === 'error') &&
									(coinbaseExt &&
									parsed.params.paymentToken === 'USDC' &&
									!coinbaseX402Ready
										? 'Preparing wallet…'
										: `Pay ${quotedUsdcLabel}`)}
							</button>
						)}
						{ready && account ? (
							<div className="mt-3 space-y-1 text-center text-xs text-on-surface-variant">
								<p>
									Connected as <span className="font-mono">{truncate(account, 6, 4)}</span>
								</p>
								{parsed.params.paymentToken === 'USDC' ? (
									<p className="flex items-center justify-center gap-1.5">
										<UsdcBaseCompositeIcon size={16} badgeSize={10} />
										<span>
											Wallet USDC:{' '}
											<span className="font-semibold text-on-surface">
												{payerUsdcBalance == null ? '…' : `$${payerUsdcBalance.toFixed(2)}`}
											</span>
											{' '}on Base
										</span>
									</p>
								) : null}
							</div>
						) : null}
						{error ? (
							<div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-200">
								{error}
							</div>
						) : null}
					</section>
				</div>
			</main>
		</div>
	)
}

function Row({
	label,
	value,
	mono = false,
	bold = false,
}: {
	label: string
	value: string
	mono?: boolean
	bold?: boolean
}) {
	return (
		<div className="flex items-center justify-between gap-4">
			<span className="text-on-surface-variant">{label}</span>
			<span className={`${mono ? 'font-mono' : ''} ${bold ? 'font-bold' : ''} text-on-surface`}>{value}</span>
		</div>
	)
}

function Divider() {
	return <div className="my-1 h-px w-full bg-outline-variant/20" />
}

/**
 * Desktop / laptop only (mobile uses `MobileWalletPayPanel` instead).
 * Do not offer go.cb-w.com / cbwallet://dapp icon buttons here — those wrappers
 * hang Coinbase Wallet extension on "Initializing". Extensions connect via the
 * injected picker once `coinbaseWalletExtension` / EIP-6963 is detected.
 */
function NoWalletPanel() {
	return (
		<div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-100">
			<p className="text-sm font-semibold">No browser wallet detected</p>
			<p className="mt-1 text-xs leading-relaxed opacity-90">
				Install the Coinbase Wallet or MetaMask browser extension, unlock it, then refresh this page. You can also
				open this link on your phone and pay inside the wallet app&apos;s built-in browser.
			</p>
		</div>
	)
}

function SuccessPanel({
	usdcTx,
	topupTx,
	tokenLabel,
	awaitingPosAuthorization,
	awaitingBeneficiaryTap,
	treasuryBridge,
	genesisSeat,
	walletDeposit,
	fuelPack,
	onDone,
}: {
	usdcTx?: string
	topupTx?: string
	tokenLabel: string
	awaitingPosAuthorization?: boolean
	awaitingBeneficiaryTap?: boolean
	treasuryBridge?: boolean
	genesisSeat?: boolean
	walletDeposit?: boolean
	fuelPack?: boolean
	onDone: () => void
}) {
	return (
		<div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-100">
			<p className="text-lg font-bold">Payment confirmed</p>
			<p className="mt-1 text-sm opacity-90">
				{fuelPack
					? `${tokenLabel} settled. B-Units (and a Genesis merchant-card NFT if this pack includes one) are being credited to the merchant account.`
					: walletDeposit
					? `${tokenLabel} settled. CoNET-USDC is being minted to your wallet via the Base–CoNET treasury bridge. Check Wallet shortly.`
					: genesisSeat
						? `${tokenLabel} settled. Your Genesis nodes are being claimed and deployed — check CoNET Mining / Wallet shortly.`
						: treasuryBridge
							? `${tokenLabel} settled to the Beamio treasury. Card points are crediting to your Smart Wallet. The merchant receives CoNET-USDC separately.`
							: awaitingBeneficiaryTap
								? `Your ${tokenLabel} payment is complete. Tap your Beamio card on the merchant terminal to finish top-up.`
								: awaitingPosAuthorization
									? `Your ${tokenLabel} payment is complete. The merchant terminal will finalize crediting your card in a moment.`
									: `${tokenLabel} transferred and your NFC card will be topped up shortly.`}
			</p>
			<div className="mt-4 grid gap-2 text-xs">
				{usdcTx ? (
					<a
						href={`https://basescan.org/tx/${usdcTx}`}
						target="_blank"
						rel="noopener noreferrer"
						className="font-mono underline hover:opacity-80"
					>
						{tokenLabel} tx: {truncate(usdcTx, 10, 8)}
					</a>
				) : null}
				{topupTx && !walletDeposit && !fuelPack ? (
					<a
						href={`https://basescan.org/tx/${topupTx}`}
						target="_blank"
						rel="noopener noreferrer"
						className="font-mono underline hover:opacity-80"
					>
						NFC topup tx: {truncate(topupTx, 10, 8)}
					</a>
				) : null}
			</div>
			<button
				type="button"
				onClick={onDone}
				className="mt-5 inline-flex w-full items-center justify-center rounded-full bg-emerald-600 px-6 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-emerald-500 active:scale-95"
			>
				Done
			</button>
		</div>
	)
}
